// Where the file and rank labels go on a board with no fixed edges.
//
// On the classic eight-by-eight this is the border every chess diagram has.
// On a grown board it cannot be a border, because the board is a *set* of
// squares and its outline is ragged — a file may start three ranks further
// north than its neighbour, and some files in the bounding box may hold no
// squares at all.
//
// So each label hangs off the outermost square of its own file or rank rather
// than off a shared edge. The result traces the board's actual silhouette,
// which is also the only placement that cannot land a label in the middle of
// the board or out in empty space beside it.

import { FILE_NAMES, fileOf, rankOf, square } from "src/engine/rules/board";

export interface GridLabel {
  /** The off-board square the label is centred on. */
  square: number;
  text: string;
  axis: "file" | "rank";
}

// Both axes count from one, all the way out.
//
// These used to switch to the *raw* coordinate outside the classic eight, to
// match `squareName`'s "f8r8" form. That put a one-based count next to a
// zero-based one on the same ruler: rank 7 read "8" and so did rank 8, and
// every rank above that was one short. The same off-by-one ran up the files,
// where 'h' was followed by "8" for what is plainly the ninth column.
//
// A label is a ruler, not an identifier. It has to count, so it counts —
// which does mean it no longer agrees with `squareName`, and that is the
// right way round: the name is for logs and tests, the label is for a person
// looking at the board.

/** Names a file: the classic letters where they exist, its number after. */
export function fileLabel(file: number): string {
  return file >= 0 && file < 8 ? FILE_NAMES[file] : String(file + 1);
}

/** Names a rank. One-based everywhere, so the count never repeats a number. */
export function rankLabel(rank: number): string {
  return String(rank + 1);
}

/**
 * A square, named the way the board's own edge labels name it.
 *
 * Not `squareName` from the rules: that is the *serialisation* format, which
 * `parseSquare` round-trips and FENs and tests are written in, so outside the
 * classic eight files it says `f9r12` on purpose. This is what a player reads
 * off the margin of the board, and the shelf has to agree with the margin.
 */
export const squareLabel = (sq: number): string => {
  const file = fileLabel(fileOf(sq));
  const rank = rankLabel(rankOf(sq));
  // `d10` is unambiguous because the file is a letter. Past the eighth file it
  // is a number too, and running them together gives "1510" for file 15 rank
  // 10 — which is not a square anyone can find on a board this size.
  return /[a-z]/.test(file) ? `${file}${rank}` : `${file}·${rank}`;
};

/**
 * A label per occupied file and per occupied rank, each one square beyond the
 * southernmost or westernmost cell of that line.
 *
 * Deterministic in the order it walks `cells`: it only ever takes minima, so
 * iteration order cannot change the answer.
 */
export function gridLabels(cells: Iterable<number>): GridLabel[] {
  const southmost = new Map<number, number>();
  const westmost = new Map<number, number>();

  for (const sq of cells) {
    const file = fileOf(sq);
    const rank = rankOf(sq);
    const south = southmost.get(file);
    if (south === undefined || rank < south) southmost.set(file, rank);
    const west = westmost.get(rank);
    if (west === undefined || file < west) westmost.set(rank, file);
  }

  const out: GridLabel[] = [];
  for (const file of [...southmost.keys()].sort((a, b) => a - b)) {
    out.push({
      square: square(file, southmost.get(file)! - 1),
      text: fileLabel(file),
      axis: "file"
    });
  }
  for (const rank of [...westmost.keys()].sort((a, b) => a - b)) {
    out.push({
      square: square(westmost.get(rank)! - 1, rank),
      text: rankLabel(rank),
      axis: "rank"
    });
  }
  return out;
}
